import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import styled from "styled-components";
import { theme } from "styles/theme";

interface DateRangePickerProps {
  visible: boolean;
  onHide: () => void;
  onApply: (fromDate: Date, toDate: Date) => void;
  initialFromDate?: Date;
  initialToDate?: Date;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
`;

const DateFieldContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const DateField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .p-calendar {
    width: 100%;
  }
`;

const Label = styled.label`
  font-weight: 500;
  color: ${theme.colors.tertiary};
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 0.85rem;
  text-align: center;
`;

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  visible,
  onHide,
  onApply,
  initialFromDate,
  initialToDate,
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(
    initialFromDate || null
  );
  const [toDate, setToDate] = useState<Date | null>(initialToDate || null);
  const [error, setError] = useState<string>("");

  // Reset dates when modal becomes visible with new initial dates
  useEffect(() => {
    if (visible) {
      setFromDate(initialFromDate || null);
      setToDate(initialToDate || null);
      setError("");
    }
  }, [visible, initialFromDate, initialToDate]);

  const validateDates = (
    fromDateParam?: Date | null,
    toDateParam?: Date | null
  ): boolean => {
    const from = fromDateParam ?? fromDate;
    const to = toDateParam ?? toDate;

    if (!from || !to) {
      setError("Please select both start and end dates");
      return false;
    }

    if (from >= to) {
      setError("Start date must be before end date");
      return false;
    }

    const maxRangeMs = 365 * 24 * 60 * 60 * 1000; // 1 year
    if (to.getTime() - from.getTime() > maxRangeMs) {
      setError("Date range cannot exceed 1 year");
      return false;
    }

    setError("");
    return true;
  };

  const handleApply = () => {
    if (validateDates() && fromDate && toDate) {
      onApply(fromDate, toDate);
      onHide();
    }
  };

  const handleFromDateChange = (date: Date | null) => {
    setFromDate(date);
    setError("");
    // Calendar will close automatically after date selection, but modal stays open
  };

  const handleToDateChange = (date: Date | null) => {
    setToDate(date);
    setError("");
    // Calendar will close automatically after date selection, but modal stays open
  };

  const footerContent = (
    <ButtonContainer>
      <div>
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          onClick={onHide}
          className="mr-2"
        />
        <Button
          label="Apply"
          severity="warning"
          onClick={handleApply}
          disabled={!fromDate || !toDate}
        />
      </div>
    </ButtonContainer>
  );

  return (
    <Dialog
      header="Select Date Range"
      visible={visible}
      style={{ width: "min(500px, 95vw)" }}
      onHide={onHide}
      footer={footerContent}
      dismissableMask
      closeOnEscape
    >
      <ModalContent>
        <DateFieldContainer>
          <DateField>
            <Label>Start Date</Label>
            <Calendar
              value={fromDate}
              onChange={(e) => handleFromDateChange(e.value as Date)}
              showTime
              hourFormat="24"
              placeholder="Select start date"
              maxDate={toDate || new Date()}
              showButtonBar
              hideOnDateTimeSelect
            />
          </DateField>

          <DateField>
            <Label>End Date</Label>
            <Calendar
              value={toDate}
              onChange={(e) => handleToDateChange(e.value as Date)}
              showTime
              hourFormat="24"
              placeholder="Select end date"
              minDate={fromDate || undefined}
              maxDate={new Date()}
              showButtonBar
              hideOnDateTimeSelect
            />
          </DateField>
        </DateFieldContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </ModalContent>
    </Dialog>
  );
};

export default DateRangePicker;
